import React, { useState } from 'react';
import { PhoneIcon, CheckIcon, ThumbsUpIcon, MessageCircleIcon, Share2Icon, PaperclipIcon } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { hasContent } from '../RichTextEditor';
import { resolveHtml, resolveText } from '../../utils/variables';
import type { NoticeVariable } from '../../types/variables';
import type { ChannelsState } from '../../types/composer';
export type PreviewTab = 'push' | 'sms' | 'email' | 'voice' | 'website' | 'social' | 'screens';
interface NoticePreviewProps {
  channels: ChannelsState;
  title: string;
  description: string;
  categoryLabel: string;
  previewTab: PreviewTab;
  onPreviewTabChange: (tab: PreviewTab) => void;
  /** Variables used to resolve chips into real values. */
  variables: NoticeVariable[];
  /** Language the notice content is written in. */
  contentLang: 'fr' | 'en';
}
export function NoticePreview({
  channels,
  title,
  description,
  categoryLabel,
  previewTab,
  onPreviewTabChange,
  variables,
  contentLang
}: NoticePreviewProps) {
  const {
    t
  } = useLanguage();
  const [voiceTestPhone, setVoiceTestPhone] = useState('');
  const [voiceTestSent, setVoiceTestSent] = useState(false);

  /** Plain text with every variable replaced by its value. */
  const text = (html: string) => resolveText(html, variables, contentLang);
  /** Rich HTML with every variable replaced by its value. */
  const rich = (html: string) => resolveHtml(html, variables, contentLang);
  const tabs: {
    id: PreviewTab;
    label: string;
  }[] = [{
    id: 'push',
    label: t('Push', 'Push')
  }, {
    id: 'sms',
    label: t('SMS', 'SMS')
  }, {
    id: 'email',
    label: t('Courriel', 'Email')
  }, {
    id: 'voice',
    label: t('Voix', 'Voice')
  }, {
    id: 'website',
    label: t('Site web', 'Website')
  }, {
    id: 'social',
    label: t('Social', 'Social')
  }, {
    id: 'screens',
    label: t('Écrans', 'Screens')
  }];
  const isPhone = previewTab === 'push' || previewTab === 'sms' || previewTab === 'voice';
  return <section className="space-y-4" data-id="element-4053" data-mp="388h9ri7hf05k">
      <div className="flex items-center justify-between gap-2 flex-wrap" data-id="element-4054" data-mp="38bmvqoktdlx2">
        <h2 className="text-base font-semibold text-slate-900" data-id="element-4055" data-mp="3d72799vcn5qb">
          {t('Aperçu', 'Preview')}
        </h2>
        <div className="flex flex-wrap gap-1 bg-slate-200/50 p-1 rounded-lg" data-id="element-4056" data-mp="3hb05zjgrieiu">
          {tabs.map(tab => <button key={tab.id} onClick={() => {
          onPreviewTabChange(tab.id);
          setVoiceTestSent(false);
        }} className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${previewTab === tab.id ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`} data-id="element-4057" data-mp="3m77d3fkw9w2d">
              {tab.label}
            </button>)}
        </div>
      </div>

      <div className="flex justify-center" data-id="element-4058" data-mp="2atmsh1pw5nzq">
        {/* Phone mockup — push / sms / voice */}
        {isPhone && <div className="w-[300px] h-[600px] bg-white rounded-[40px] border-[8px] border-slate-900 overflow-hidden relative" data-id="element-4059" data-mp="239knn3nvmm7u">
            <div className="absolute top-0 inset-x-0 h-6 flex justify-center z-20" data-id="element-4060" data-mp="1ycc0sprs9u09">
              <div className="w-32 h-6 bg-slate-900 rounded-b-xl" data-id="element-4061" data-mp="1tav2n20oolnd" />
            </div>

            <div className="w-full h-full bg-slate-50 pt-12 px-4 relative" data-id="element-4062" data-mp="1p9t3smaoiblx">
              {previewTab === 'push' && <div className="bg-white/80 backdrop-blur-md rounded-2xl p-4 border border-slate-100 mt-4" data-id="element-4063" data-mp="1kdcgfubb019w">
                  <div className="flex items-center gap-2 mb-2" data-id="element-4064" data-mp="1fjq7ac4cmfiu">
                    <img src="https://cdn.magicpatterns.com/uploads/ckK2136PdZbnMQPqZiNST7/image.png" alt={t('Logo Ville de Windsor', 'City of Windsor logo')} className="w-5 h-5 rounded object-cover flex-shrink-0" data-id="element-4065" data-mp="1amzv3zvo042p" />
                    <span className="text-xs font-medium text-slate-500 truncate" data-id="element-4066" data-mp="wn8dzq9uq7kh">
                      {t('Ville de Windsor', 'City of Windsor')}
                    </span>
                    <span className="text-xs text-slate-400 ml-auto" data-id="element-4067" data-mp="30qjkvo38ea28">
                      {t('Maintenant', 'Now')}
                    </span>
                  </div>
                  <h4 className="text-sm font-semibold text-slate-900 mb-1" data-id="element-4068" data-mp="2mwsn1cyv4emc">
                    {text(channels.push.title) || title || t('Titre de la notification', 'Notification title')}
                  </h4>
                  <p className="text-sm text-slate-600 leading-snug" data-id="element-4069" data-mp="2i0bodou1ehv0">
                    {text(channels.push.message) || description || t('Le message de votre notification apparaîtra ici…', 'Your notification message will appear here…')}
                  </p>
                </div>}

              {previewTab === 'sms' && <div className="mt-8 space-y-4" data-id="element-4070" data-mp="hbfk96e3gx2g">
                  <div className="flex justify-center" data-id="element-4071" data-mp="hbxmqkrco7it">
                    <span className="text-[10px] font-medium text-slate-400" data-id="element-4072" data-mp="cckxxugx4qdl">
                      {t("Aujourd'hui 10:42", 'Today 10:42')}
                    </span>
                  </div>
                  <div className="bg-blue-500 text-white rounded-2xl rounded-tr-sm p-3 max-w-[85%] ml-auto" data-id="element-4073" data-mp="ld0zgye3pnft">
                    <p className="text-sm leading-snug" data-id="element-4074" data-mp="101e0wavyjyhp">
                      {text(channels.sms.text) || t('Votre message texte apparaîtra ici. Il doit être court et direct.', 'Your text message will appear here. Keep it short and direct.')}
                    </p>
                  </div>
                </div>}

              {previewTab === 'voice' && <div className="absolute inset-0 bg-[#0F172A] pt-14 px-5 flex flex-col" data-id="element-4075" data-mp="3avrwbsab5owb">
                  <div className="flex flex-col items-center text-center" data-id="element-4076" data-mp="36ug219ghofhs">
                    <span className="text-xs font-medium text-slate-400 uppercase tracking-widest mb-4" data-id="element-4077" data-mp="19tabta5qh5b9">
                      {t('Appel entrant', 'Incoming call')}
                    </span>
                    <div className="w-20 h-20 rounded-full bg-white flex items-center justify-center mb-3" data-id="element-4078" data-mp="1jp3s94mkpiyb">
                      <PhoneIcon className="w-8 h-8 text-[#4F46E5]" data-id="element-4079" data-mp="1oqkrywtbmzdb" />
                    </div>
                    <h4 className="text-base font-semibold text-white" data-id="element-4080" data-mp="1sq205y04uam6">
                      {t('Ville de Windsor', 'City of Windsor')}
                    </h4>
                    <p className="text-xs text-slate-400 mb-4" data-id="element-4081" data-mp="22fv36am7lhet">
                      {t('Message vocal automatisé', 'Automated voice message')}
                    </p>
                  </div>

                  <div className="bg-white/10 rounded-2xl p-3 text-left mb-4 overflow-y-auto max-h-32" data-id="element-4082" data-mp="2cf43rmbkxua5">
                    <p className="text-xs text-slate-200 leading-relaxed" data-id="element-4083" data-mp="2gfo04nobwtv8">
                      {text(channels.voice.script) || t('Le script de votre appel vocal sera lu ici par une voix automatisée…', 'Your call script will be read here by an automated voice…')}
                    </p>
                  </div>

                  <div className="mt-auto bg-white rounded-2xl p-4 mb-4" data-id="element-4084" data-mp="3mzgx4mzkhfh4">
                    {voiceTestSent ? <div className="text-center" data-id="element-4085" data-mp="oga12vtngt1k">
                        <div className="w-9 h-9 rounded-full bg-emerald-50 flex items-center justify-center mx-auto mb-2" data-id="element-4086" data-mp="jhysspj06qlg">
                          <CheckIcon className="w-5 h-5 text-emerald-600" data-id="element-4087" data-mp="epyl2awoyteb" />
                        </div>
                        <p className="text-sm font-semibold text-slate-900" data-id="element-4088" data-mp="9qvt9s5q2joa">
                          {t('Test envoyé !', 'Test sent!')}
                        </p>
                        <p className="text-xs text-slate-500" data-id="element-4089" data-mp="12gulfwf7qwx6">
                          {t('Un appel test est en route vers ', 'A test call is on its way to ')}
                          {voiceTestPhone}.
                        </p>
                        <button onClick={() => setVoiceTestSent(false)} className="mt-2 text-xs font-semibold text-[#4F46E5] hover:text-[#4338CA]" data-id="element-4090" data-mp="1t4h092sszqu">
                          {t('Envoyer un autre test', 'Send another test')}
                        </button>
                      </div> : <>
                        <label className="block text-xs font-semibold text-slate-700 mb-1.5" data-id="element-4091" data-mp="khrn8uwpnv60">
                          {t("Tester l'appel vocal", 'Test the voice call')}
                        </label>
                        <input type="tel" value={voiceTestPhone} onChange={e => setVoiceTestPhone(e.target.value)} placeholder="(514) 555-0199" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#4F46E5] focus:ring-1 focus:ring-[#4F46E5] mb-2" data-id="element-4092" data-mp="3588vcc3jh2lt" />
                        <button onClick={() => {
                  if (voiceTestPhone.trim()) setVoiceTestSent(true);
                }} disabled={!voiceTestPhone.trim()} className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-semibold text-white bg-[#4F46E5] rounded-lg hover:bg-[#4338CA] transition-colors disabled:opacity-40 disabled:cursor-not-allowed" data-id="element-4093" data-mp="36q7fknmltgki">
                          <PhoneIcon className="w-4 h-4" data-id="element-4094" data-mp="209vikpviq4qi" />{' '}
                          {t('Envoyer un test', 'Send a test')}
                        </button>
                      </>}
                  </div>
                </div>}
            </div>
          </div>}

        {/* Social post mockup */}
        {previewTab === 'social' && <div className="w-full max-w-[360px] bg-white rounded-xl border border-slate-200 overflow-hidden shadow-sm" data-id="element-4095" data-mp="hlr90ay3oenx">
            <div className="flex items-center gap-3 p-3 border-b border-slate-100" data-id="element-4096" data-mp="7po3cv41hxw8">
              <img src="https://cdn.magicpatterns.com/uploads/kfqfiE7nmccz961Hy3x3v1/image.png" alt={t('Logo Ville de Windsor', 'City of Windsor logo')} className="w-9 h-9 rounded-full object-cover flex-shrink-0" data-id="element-4097" data-mp="3nu6kxwyte1c" />
              <div className="min-w-0" data-id="element-4098" data-mp="3q1avmr49myu0">
                <p className="text-sm font-semibold text-slate-900 truncate" data-id="element-4099" data-mp="3l4u89z4w4ohz">
                  {t('Ville de Windsor', 'City of Windsor')}
                </p>
                <p className="text-[11px] text-slate-400" data-id="element-4100" data-mp="3g7bvtyrnzr58">
                  {t("À l'instant · ", 'Just now · ')}
                  <span aria-hidden="true" data-id="element-4101" data-mp="3c606lucpd7xk">🌐</span>
                </p>
              </div>
            </div>

            <div className="px-3 py-2" data-id="element-4102" data-mp="1vn6v91u3wr47">
              {hasContent(channels.social.postText) ? <div className="text-sm text-slate-700 leading-snug [&_a]:text-[#4F46E5] [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_b]:font-semibold [&_strong]:font-semibold [&_img]:w-full [&_img]:rounded-lg [&_img]:my-2" dangerouslySetInnerHTML={{
            __html: rich(channels.social.postText)
          }} data-id="element-4103" data-mp="20m9vm8z5695u" /> : <p className="text-sm text-slate-400" data-id="element-4104" data-mp="1cwyx6y59kquv">
                  {t('Le texte de votre publication apparaîtra ici.', 'Your post text will appear here.')}
                </p>}
            </div>

            <div className="flex items-center justify-around px-3 py-2 border-t border-slate-100 text-slate-500" data-id="element-4105" data-mp="p6bklzkeld1z">
              <span className="inline-flex items-center gap-1.5 text-xs font-medium" data-id="element-4106" data-mp="u4d2wia098ce">
                <ThumbsUpIcon className="w-4 h-4" data-id="element-4107" data-mp="m3yoy0uya5kx" /> {t("J'aime", 'Like')}
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs font-medium" data-id="element-4108" data-mp="i3527at8uwtm">
                <MessageCircleIcon className="w-4 h-4" data-id="element-4109" data-mp="vu9rpk2we8q8" /> {t('Commenter', 'Comment')}
              </span>
              <span className="inline-flex items-center gap-1.5 text-xs font-medium" data-id="element-4110" data-mp="qx9pxld2zs4x">
                <Share2Icon className="w-4 h-4" data-id="element-4111" data-mp="35vwfc371qid" /> {t('Partager', 'Share')}
              </span>
            </div>
          </div>}

        {/* Public screen mockup — 16:9 landscape signage */}
        {previewTab === 'screens' && <div className="w-full max-w-[400px]" data-id="element-4112" data-mp="d64rgmlg95w6">
            <div className="rounded-xl bg-slate-900 p-2.5 shadow-sm" data-id="element-4113" data-mp="857b8fk02lxb">
              <div className="aspect-video rounded-lg bg-[#0F172A] overflow-hidden flex flex-col" data-id="element-4114" data-mp="1ok08y1zttqqk">
                <div className="flex items-center justify-between px-4 pt-3.5 flex-shrink-0" data-id="element-4115" data-mp="1jkw23i0dk53e">
                  <img src="https://cdn.magicpatterns.com/uploads/kfqfiE7nmccz961Hy3x3v1/image.png" alt={t('Logo Ville de Windsor', 'City of Windsor logo')} className="h-6 w-6 rounded object-cover bg-white" data-id="element-4116" data-mp="1jn9xjryyvjbh" />
                  <span className="text-[10px] font-semibold text-slate-400 uppercase tracking-wider" data-id="element-4117" data-mp="2sjx93hm7wvwq">
                    {categoryLabel}
                  </span>
                </div>

                <div className="flex-1 flex flex-col justify-center px-5 py-3 min-h-0" data-id="element-4118" data-mp="3l7b6thxqpvhw">
                  {hasContent(channels.screens.headline) ? <p className="text-lg font-bold text-white leading-tight line-clamp-2" data-id="element-4119" data-mp="3bh860vkdnd90">
                      {text(channels.screens.headline)}
                    </p> : <p className="text-lg font-bold text-slate-600 leading-tight" data-id="element-4120" data-mp="7zg7szfndx2d">
                      {title || t('Avis important', 'Important notice')}
                    </p>}
                  {hasContent(channels.screens.bodyText) ? <p className="mt-2 text-[11px] text-slate-300 leading-snug line-clamp-4" data-id="element-4121" data-mp="2k86le1u03zlb">
                      {text(channels.screens.bodyText)}
                    </p> : <p className="mt-2 text-[11px] text-slate-500 leading-snug" data-id="element-4122" data-mp="2bbp8ou5zntng">
                      {t('Votre message apparaîtra ici, en gros caractères.', 'Your message will appear here, in large type.')}
                    </p>}
                </div>

                <div className="px-4 pb-3 flex items-center justify-between flex-shrink-0" data-id="element-4123" data-mp="2u7npwfxio7fp">
                  <span className="text-[9px] text-slate-500" data-id="element-4124" data-mp="2p9vz5p8lyrd3">
                    {t('windsor.ca', 'windsor.ca')}
                  </span>
                  {/* Slide progress bar */}
                  <span className="h-0.5 w-20 rounded-full bg-slate-700 overflow-hidden" data-id="element-4125" data-mp="3hybbg40cuvkq">
                    <span className="block h-full w-2/3 bg-[#4F46E5]" data-id="element-4126" data-mp="3p966vr0nfj0f" />
                  </span>
                </div>
              </div>
            </div>

            <p className="mt-2.5 text-xs text-slate-500 text-center" data-id="element-4127" data-mp="3u8sumue3shnh">
              {t(`Rotation de ${channels.screens.duration} s sur ${channels.screens.locations.length} écran${channels.screens.locations.length > 1 ? 's' : ''}`, `${channels.screens.duration} s rotation on ${channels.screens.locations.length} screen${channels.screens.locations.length > 1 ? 's' : ''}`)}
            </p>
          </div>}

        {/* Desktop mockup — email / website */}
        {(previewTab === 'email' || previewTab === 'website') && <div className="w-full max-w-[360px]" data-id="element-4128" data-mp="qqiq18szz6v7">
            <div className="bg-white rounded-xl border border-slate-300 overflow-hidden shadow-sm" data-id="element-4129" data-mp="qtdcf5vkn1r9">
              <div className="h-9 bg-slate-100 border-b border-slate-200 flex items-center gap-1.5 px-3" data-id="element-4130" data-mp="urb209t97pv6">
                <span className="w-2.5 h-2.5 rounded-full bg-red-400" data-id="element-4131" data-mp="2vvd83s260i1y" />
                <span className="w-2.5 h-2.5 rounded-full bg-amber-400" data-id="element-4132" data-mp="2vvmyhgr4gr86" />
                <span className="w-2.5 h-2.5 rounded-full bg-emerald-400" data-id="element-4133" data-mp="2rv32igiabtnm" />
                <div className="ml-2 flex-1 h-5 bg-white rounded border border-slate-200 text-[9px] text-slate-400 flex items-center px-2 truncate" data-id="element-4134" data-mp="35lpyw20kp7gj">
                  {previewTab === 'email' ? 'mail.windsor.ca' : 'windsor.ca'}
                </div>
              </div>

              <div className="bg-white h-[440px] overflow-y-auto relative" data-id="element-4135" data-mp="3fl730f06fdae">
                {previewTab === 'email' && <div className="bg-white" data-id="element-4136" data-mp="3okkhxn72ku2i">
                    <div className="px-5 py-3 border-b border-slate-100" data-id="element-4137" data-mp="3oj9e5ndmjmbe">
                      <p className="text-xs text-slate-500 mb-1" data-id="element-4138" data-mp="3jm0utjgqpc8k">
                        {t('De: Ville de Windsor', 'From: City of Windsor')}
                      </p>
                      <h4 className="text-sm font-semibold text-slate-900" data-id="element-4139" data-mp="1p76mhpuujlmn">
                        {text(channels.email.subject) || title || t('Objet du courriel', 'Email subject')}
                      </h4>
                    </div>
                    <div className="p-6" data-id="element-4140" data-mp="3s1ydjcggkmww">
                      <img src="https://cdn.magicpatterns.com/uploads/nedc6b9KaGTagFGJXbwvij/pasted-image.png" alt={t('Logo Ville de Windsor', 'City of Windsor logo')} className="h-8 w-auto object-contain mb-5" data-id="element-4141" data-mp="3s0x051byzoc0" />

                      <h1 className="text-xl font-bold text-slate-900 mb-3" data-id="element-4142" data-mp="3n3oir2ynbobt">
                        {title || t("Titre de l'avis", 'Notice title')}
                      </h1>

                      {hasContent(channels.email.body) ? <div className="text-sm text-slate-600 leading-relaxed [&_a]:text-[#4F46E5] [&_a]:underline [&_ul]:list-disc [&_ul]:pl-5 [&_b]:font-semibold [&_strong]:font-semibold [&_img]:w-full [&_img]:rounded-lg [&_img]:my-3" dangerouslySetInnerHTML={{
                  __html: rich(channels.email.body)
                }} data-id="element-4143" data-mp="3d6iqhreapra0" /> : <p className="text-sm text-slate-400 leading-relaxed" data-id="element-4144" data-mp="3ljk6r2xsmsqw">
                          {description || t("Le contenu détaillé de votre courriel s'affichera ici.", 'The detailed content of your email will appear here.')}
                        </p>}

                      {channels.email.attachments.length > 0 && <div className="mt-5 pt-4 border-t border-slate-100" data-id="element-4145" data-mp="32msh8brnrhee">
                          <p className="text-[11px] font-semibold text-slate-400 uppercase tracking-wide mb-2" data-id="element-4146" data-mp="2xn6380409x4j">
                            {channels.email.attachments.length}{' '}
                            {t('pièce(s) jointe(s)', 'attachment(s)')}
                          </p>
                          <ul className="space-y-1.5" data-id="element-4147" data-mp="14a7j2tjyxbju">
                            {channels.email.attachments.map(file => <li key={file.id} className="flex items-center gap-2 border border-slate-200 rounded-md px-2.5 py-1.5" data-id="element-4148" data-mp="18b17do1bosd9">
                                <PaperclipIcon className="w-3.5 h-3.5 text-slate-400 flex-shrink-0" data-id="element-4149" data-mp="lceg5r9ch1mg" />
                                <span className="text-xs text-slate-600 truncate" data-id="element-4150" data-mp="pdq6jz01lqvp">
                                  {file.name}
                                </span>
                              </li>)}
                          </ul>
                        </div>}
                    </div>
                  </div>}

                {previewTab === 'website' && <div className="relative" data-id="element-4151" data-mp="2aq9qh4tx8i8o">
                    <img src="https://cdn.magicpatterns.com/uploads/paDPkCcwNHTw4rcW21cM4q/image.png" alt={t('Aperçu du site web de Windsor', 'Preview of the Windsor website')} className="w-full block" data-id="element-4152" data-mp="2atfdmeizpa1r" />
                    <div className="absolute top-[13%] inset-x-0 bg-[#E7F1FB] px-3 py-3 border-y border-[#cfe0f5]" data-id="element-4153" data-mp="54cytatgsrk6">
                      <div className="flex items-start justify-between gap-2" data-id="element-4154" data-mp="33p4q6ttit0yn">
                        <div className="min-w-0" data-id="element-4155" data-mp="38n68vdn1ay9l">
                          <span className="inline-block px-3 py-0.5 rounded-full bg-[#1D4ED8] text-white text-[10px] font-bold mb-1.5" data-id="element-4156" data-mp="3dlhhxm5i94qr">
                            {categoryLabel}
                          </span>
                          <p className="text-[11px] font-bold text-slate-900 leading-snug" data-id="element-4157" data-mp="svimud9k6q5k">
                            {text(channels.website.bannerText) || title || t('Le texte de votre bannière apparaîtra ici', 'Your banner text will appear here')}
                          </p>
                          <p className="text-[10px] font-semibold text-slate-800 underline mt-1" data-id="element-4158" data-mp="28l9sta0a3n2z">
                            {text(channels.website.ctaLabel) || t("Consulter l'avis en cours", 'View the current notice')}
                          </p>
                        </div>
                        <span className="text-slate-500 text-sm leading-none" data-id="element-4159" data-mp="1tnqyzt8qujoo">✕</span>
                      </div>
                    </div>
                  </div>}
              </div>
            </div>

            <div className="mx-auto h-2.5 w-[88%] bg-slate-300 rounded-b-xl" data-id="element-4160" data-mp="1kqoztxk85vtw" />
            <div className="mx-auto h-1 w-[40%] bg-slate-200 rounded-b-lg" data-id="element-4161" data-mp="3k17yr505tszz" />
          </div>}
      </div>
    </section>;
}